// セキュアログシステム - 本番環境での機密情報漏洩を防ぐ

interface LogLevel {
  DEBUG: 0;
  INFO: 1;
  WARN: 2;
  ERROR: 3;
  NONE: 4;
}

const LOG_LEVELS: LogLevel = {
  DEBUG: 0,
  INFO: 1,
  WARN: 2,
  ERROR: 3,
  NONE: 4
};

// 本番環境かどうかを判定
const isProduction = (): boolean => {
  return process.env.NODE_ENV === 'production' || 
         import.meta.env.PROD ||
         (typeof window !== 'undefined' && (
           window.location.hostname === 'pixelart.tokyo' ||
           window.location.hostname === 'photo-2-card-game-maker.web.app' ||
           window.location.hostname.includes('firebaseapp.com') ||
           (window.location.hostname !== 'localhost' && !window.location.hostname.includes('127.0.0.1'))
         ));
};

// 本番環境でのログレベル（完全無効化）
const getLogLevel = (): number => {
  if (isProduction()) {
    return LOG_LEVELS.NONE; // 本番では完全に無効化
  }
  return LOG_LEVELS.DEBUG; // 開発では全て
};

// 機密情報をマスキングする関数
const maskSensitiveInfo = (message: string): string => {
  if (!isProduction()) return message;
  
  return message
    .replace(/AIzaSy[A-Za-z0-9_-]{33}/g, 'API_KEY_[MASKED]') // Firebase API Key
    .replace(/ya29\.[A-Za-z0-9_-]+/g, 'ACCESS_TOKEN_[MASKED]') // Google Access Token
    .replace(/"apiKey":\s*"[^"]+"/g, '"apiKey": "[MASKED]"') // JSON内のAPIキー
    .replace(/GEMINI_API_KEY[=:]\s*[A-Za-z0-9_-]+/g, 'GEMINI_API_KEY=[MASKED]') // Gemini APIキー
    .replace(/Bearer\s+[A-Za-z0-9_-]+/g, 'Bearer [MASKED]') // Bearer Token
    .replace(/password[=:]\s*[^\s&]+/gi, 'password=[MASKED]') // パスワード
    .replace(/token[=:]\s*[^\s&]+/gi, 'token=[MASKED]'); // 一般的なトークン
};

// エラーオブジェクトから安全な情報のみを抽出
const sanitizeError = (error: any): any => {
  if (!isProduction()) return error;
  
  if (error instanceof Error) {
    return {
      name: error.name,
      message: maskSensitiveInfo(error.message),
      // スタックトレースは本番では除外
    };
  }
  
  if (typeof error === 'object' && error !== null) {
    const sanitized: any = {};
    
    // 安全なプロパティのみコピー
    const safeProps = ['status', 'statusText', 'code', 'type', 'name', 'message'];
    for (const prop of safeProps) {
      if (prop in error) {
        sanitized[prop] = typeof error[prop] === 'string' 
          ? maskSensitiveInfo(error[prop]) 
          : error[prop];
      }
    }
    
    return sanitized;
  }
  
  return typeof error === 'string' ? maskSensitiveInfo(error) : '[OBJECT]';
};

// セキュアログクラス
export class SecureLogger {
  private static currentLevel = getLogLevel();
  
  static debug(message: string, ...args: any[]): void {
    if (this.currentLevel <= LOG_LEVELS.DEBUG) {
      console.log(`[DEBUG] ${maskSensitiveInfo(message)}`, ...args.map(sanitizeError));
    }
  }
  
  static info(message: string, ...args: any[]): void {
    if (this.currentLevel <= LOG_LEVELS.INFO) {
      console.log(`[INFO] ${maskSensitiveInfo(message)}`, ...args.map(sanitizeError));
    }
  }
  
  static warn(message: string, ...args: any[]): void {
    if (this.currentLevel <= LOG_LEVELS.WARN) {
      console.warn(`[WARN] ${maskSensitiveInfo(message)}`, ...args.map(sanitizeError));
    }
  }
  
  static error(message: string, ...args: any[]): void {
    if (this.currentLevel <= LOG_LEVELS.ERROR) {
      console.error(`[ERROR] ${maskSensitiveInfo(message)}`, ...args.map(sanitizeError));
    }
  }
  
  // 本番環境では何も出力しない完全に無音のログ
  static silent(message: string, ...args: any[]): void {
    if (!isProduction()) {
      console.log(`[SILENT] ${message}`, ...args);
    }
  }
}

// 便利な短縮形
export const logger = SecureLogger;
